import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Table,
  Tag,
  Modal,
  message,
  Spin,
  Empty,
  Alert,
} from 'antd';
import { WalletOutlined, GiftOutlined, HistoryOutlined, SwapOutlined } from '@ant-design/icons';
import { walletService, loyaltyService } from '../services/api';
import { extractList } from '../utils/formatters';
import './CustomerDashboard.css';

const { Title, Paragraph, Text } = Typography;

const labelStyle = {
  fontSize: '0.72rem',
  fontWeight: 600,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  color: '#6b7280',
  marginBottom: 2,
};

const iconWrapStyle = (background, shadow) => ({
  width: 48,
  height: 48,
  borderRadius: 12,
  flexShrink: 0,
  background,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: `0 6px 16px ${shadow}`,
});

const formatNPR = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'NPR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
};

const Wallet = () => {
  const [loading] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [redemptionOptions, setRedemptionOptions] = useState(null);
  const [redeemModal, setRedeemModal] = useState(false);
  const [redeemPoints, setRedeemPoints] = useState(0);
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    fetchWallet();
    fetchTransactions();
    fetchLoyaltyData();
    fetchRedemptionOptions();
  }, []);

  const fetchWallet = async () => {
    try {
      const response = await walletService.getWallet();
      setWallet(response.data?.data || response.data);
    } catch (error) {
      console.error('Error fetching wallet:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await walletService.getTransactions();
      setTransactions(extractList(response.data));
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const fetchLoyaltyData = async () => {
    try {
      const response = await loyaltyService.getLoyaltyPoints();
      setLoyaltyData(response.data?.data || response.data);
    } catch (error) {
      console.error('Error fetching loyalty data:', error);
    }
  };

  const fetchRedemptionOptions = async () => {
    try {
      const response = await loyaltyService.getRedemptionOptions();
      setRedemptionOptions(response.data?.data || response.data);
    } catch (error) {
      console.error('Error fetching redemption options:', error);
    }
  };

  const handleRedeem = async () => {
    if (!redeemPoints || redeemPoints <= 0) {
      message.warning('Please enter points to redeem');
      return;
    }
    setRedeeming(true);
    try {
      await loyaltyService.redeemPoints(redeemPoints, 'wallet');
      message.success(`Successfully redeemed ${redeemPoints} points!`);
      setRedeemModal(false);
      setRedeemPoints(0);
      fetchWallet();
      fetchTransactions();
      fetchLoyaltyData();
    } catch (error) {
      // The loyalty API reports failures with an `error` key, not `message`.
      message.error(
        error.response?.data?.error || error.response?.data?.message || 'Failed to redeem points',
      );
    } finally {
      setRedeeming(false);
    }
  };

  const typeColors = {
    credit: '#52b788',
    debit: '#e5484d',
    loyalty_conversion: '#f1a134',
    referral_bonus: '#2e9bd6',
    withdrawal: '#2e7eba',
  };

  const typeLabels = {
    credit: 'Credit',
    debit: 'Debit',
    loyalty_conversion: 'Loyalty',
    referral_bonus: 'Referral',
    withdrawal: 'Withdrawal',
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (d) => (d ? new Date(d).toLocaleString() : '-'),
      width: 170,
    },
    {
      title: 'Type',
      dataIndex: 'transaction_type',
      key: 'type',
      render: (t) => <Tag color={typeColors[t] || 'default'}>{typeLabels[t] || t}</Tag>,
      width: 120,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (d) => d || '-',
    },
    {
      title: 'Order ID',
      dataIndex: 'order_id',
      key: 'order_id',
      render: (id) => (id ? <Tag>{id}</Tag> : '-'),
      width: 100,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount, record) => {
        const isCredit =
          record.transaction_type === 'credit' ||
          record.transaction_type === 'referral_bonus' ||
          record.transaction_type === 'loyalty_conversion';
        return (
          <Text strong style={{ color: isCredit ? '#52b788' : '#e5484d' }}>
            {isCredit ? '+' : '-'}
            {formatNPR(Math.abs(amount || 0))}
          </Text>
        );
      },
      width: 130,
    },
  ];

  if (loading && !wallet) {
    return (
      <div className="customer-page" style={{ padding: 0, textAlign: 'center', paddingTop: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  const balance = wallet?.balance || 0;
  const loyaltyBalance = loyaltyData?.current_balance || loyaltyData?.balance || 0;
  const rateLabel = redemptionOptions?.redemption_rate || '';
  const rate = redemptionOptions?.redemption_rate_value || 1;
  const minRedemption =
    redemptionOptions?.minimum_redemption || redemptionOptions?.minimum_points || 0;
  const maxRedemption =
    redemptionOptions?.max_redemption_per_order ||
    redemptionOptions?.max_points_per_order ||
    loyaltyBalance;

  return (
    <div className="customer-page" style={{ padding: 0 }}>
      <div className="customer-page-header">
        <Title level={2}>My Wallet</Title>
        <Paragraph>Your wallet balance and loyalty points.</Paragraph>
      </div>

      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} sm={12}>
          <Card
            className="customer-card animate-fade-slide-up animate-stagger-1"
            style={{ height: '100%' }}
            bodyStyle={{
              padding: 20,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div
                style={iconWrapStyle(
                  'linear-gradient(135deg, #52b788, #2d5a3d)',
                  'rgba(82, 183, 136, 0.25)',
                )}
              >
                <WalletOutlined style={{ fontSize: 22, color: '#fff' }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={labelStyle}>Available Balance</div>
                <div
                  style={{
                    fontSize: '1.65rem',
                    fontWeight: 700,
                    color: '#2d5a3d',
                    lineHeight: 1.2,
                    wordBreak: 'break-word',
                  }}
                >
                  {formatNPR(balance)}
                </div>
              </div>
            </div>
            <div
              style={{
                marginTop: 'auto',
                paddingTop: 14,
                fontSize: '0.8rem',
                color: '#9ca3af',
              }}
            >
              Spendable across all orders and services.
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12}>
          <Card
            className="customer-card animate-fade-slide-up animate-stagger-2"
            style={{ height: '100%' }}
            bodyStyle={{
              padding: 20,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div
                style={iconWrapStyle(
                  'linear-gradient(135deg, #f1a134, #e08a10)',
                  'rgba(241, 161, 52, 0.25)',
                )}
              >
                <GiftOutlined style={{ fontSize: 22, color: '#fff' }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={labelStyle}>Loyalty Points</div>
                <div
                  style={{
                    fontSize: '1.65rem',
                    fontWeight: 700,
                    color: '#f1a134',
                    lineHeight: 1.2,
                  }}
                >
                  {loyaltyBalance}
                </div>
              </div>
            </div>
            <div
              style={{
                marginTop: 'auto',
                paddingTop: 14,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                {redemptionOptions
                  ? rateLabel || `1 point = NPR ${rate}`
                  : 'Earn points on every order.'}
              </span>
              <Button
                type="primary"
                icon={<SwapOutlined />}
                onClick={() => setRedeemModal(true)}
                disabled={loyaltyBalance < minRedemption}
                style={{
                  background: 'linear-gradient(135deg, #f1a134, #e08a10)',
                  border: 'none',
                  borderRadius: 8,
                  fontWeight: 600,
                }}
              >
                Redeem Points
              </Button>
            </div>
          </Card>
        </Col>
      </Row>

      <Card
        className="customer-card"
        title={<span style={{ color: '#2d5a3d', fontWeight: 600 }}>Transaction History</span>}
        extra={
          <Button
            className="customer-btn-secondary"
            icon={<HistoryOutlined />}
            size="small"
            onClick={fetchTransactions}
          >
            Refresh
          </Button>
        }
        style={{ marginTop: 16 }}
      >
        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="id"
          pagination={{ pageSize: 10, size: 'small' }}
          locale={{ emptyText: <Empty description="No transactions yet" /> }}
        />
      </Card>

      <Modal
        title="Redeem Loyalty Points"
        open={redeemModal}
        onCancel={() => {
          setRedeemModal(false);
          setRedeemPoints(0);
        }}
        footer={null}
        width={400}
      >
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: '1rem', color: '#6b7280', marginBottom: 4 }}>
            Available Points
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#e08a10' }}>
            {loyaltyBalance}
          </div>
          {redemptionOptions && (
            <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginTop: 8 }}>
              {rateLabel || `1 point = Rs.${rate}`}
            </div>
          )}
          {minRedemption > 0 && (
            <div style={{ fontSize: '0.85rem', color: '#9ca3af' }}>
              Minimum redemption: {minRedemption} points
            </div>
          )}
        </div>

        {redemptionOptions?.redemption_enabled === false ? (
          <Alert message="Redemption is currently disabled" type="warning" showIcon />
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: 12 }}>
              Select points to redeem
            </div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 10,
                justifyContent: 'center',
                marginBottom: 16,
              }}
            >
              {(
                redemptionOptions?.redemption_presets || [50, 100, 150, 200, 250, 300, 500, 1000]
              ).map((p) => (
                <Button
                  key={p}
                  onClick={() => setRedeemPoints(p)}
                  type={redeemPoints === p ? 'primary' : 'default'}
                  disabled={p > loyaltyBalance || p > maxRedemption}
                  size="large"
                  style={{
                    minWidth: 80,
                    borderRadius: 8,
                    fontWeight: redeemPoints === p ? 700 : 500,
                    ...(redeemPoints === p
                      ? {
                          background: 'linear-gradient(135deg, #f1a134, #e08a10)',
                          borderColor: '#e08a10',
                        }
                      : {}),
                  }}
                >
                  {p}
                </Button>
              ))}
            </div>
            {redeemPoints > 0 && (
              <div
                style={{
                  background: '#fef4e3',
                  borderRadius: 8,
                  padding: '10px 16px',
                  marginBottom: 16,
                  fontSize: '1rem',
                  color: '#8a5a12',
                }}
              >
                You will receive <strong>{formatNPR(redeemPoints * rate)}</strong>
              </div>
            )}
            <Button
              type="primary"
              onClick={handleRedeem}
              loading={redeeming}
              block
              size="large"
              disabled={redeemPoints < minRedemption || redeemPoints > loyaltyBalance}
              style={{
                background: 'linear-gradient(135deg, #f1a134, #e08a10)',
                border: 'none',
                borderRadius: 8,
                fontWeight: 600,
                height: 46,
              }}
            >
              <SwapOutlined style={{ marginRight: 6 }} />
              Convert to Wallet Balance
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Wallet;
